import { MemberType, OccType, RoomCleanStatus, RoomMainStatus } from '@/config/constant';
import BaseService from '@/api/checkInBase';
import DateJS, { format2DateStr, format2TimeStamp } from '@/utils/date';
import {
  ConfigRoomPrice,
  RentRoomInfo,
  RentTimeInfo,
  RoomCategory,
  RemarkInfo,
  NoticeInfo,
  CheckInGuestInfo,
  RoomItem,
  CouponValue,
} from '@/redux/reducers/reservation/base.d';
import { ReducerState, MainState, storeName } from '@/redux/reducers/reservation/checkInByReserve';
import { parseFen2Yuan } from '@/utils/number';
import { renderIntlLabel } from '@/utils';

import { CheckInByReserveAction } from './constant';
import {
  createActionMiddleware,
  getBusinessDate,
  getEmptyCouponData,
  filterArrByKey,
  findItemByKey,
  createGuestItem,
  flatPriceList,
  getAllChannelList,
  batchCalcCouponValue,
  formatAssignRoomList,
} from './helper';

// getState函数覆写
const actionWrapper = createActionMiddleware<ReducerState>(storeName);

// 获取可选房间
const getAllRoomList: (timeInfo: RentTimeInfo) => Promise<RoomCategory[]> = async (timeInfo) => {
  let result: RoomCategory[] = [];
  try {
    const { expectedArrivalTime, rentTime, expectedDepartureTime } = timeInfo;
    if (expectedArrivalTime && rentTime) {
      const res = await BaseService.getRoomList({
        expectedArrivalTime: format2TimeStamp(expectedArrivalTime),
        expectedDepartureTime: format2TimeStamp(expectedDepartureTime),
      });
      // result = res.data || [];
      result = formatAssignRoomList(res.data) || [];
    }
  } catch (_) {
    result = [];
  }
  return result;
};

// 获取房间定价
const getRoomPriceList: (state: ReducerState) => Promise<ConfigRoomPrice[]> = async (state) => {
  let result: ConfigRoomPrice[] = [];
  try {
    const { channelInfo, commonInfo, rentTimeInfo, rentRoomInfos } = state;
    if (rentRoomInfos.length && channelInfo.channelId) {
      const roomTypeIds: number[] = []; // 去重的房型列表
      const typeIdMap: { [key: number]: boolean } = {}; // 房型id map
      (rentRoomInfos || []).forEach((item) => {
        const { roomTypeId } = item;
        // 已有的房型id
        if (typeIdMap[roomTypeId]) return;
        roomTypeIds.push(roomTypeId);
        typeIdMap[roomTypeId] = true;
      });
      const res = await BaseService.getRoomPrice({
        channelId: channelInfo.channelId,
        endDate: format2DateStr(rentTimeInfo.expectedDepartureTime),
        occDuration: commonInfo.occType === OccType.DayUse ? undefined : rentTimeInfo.rentTime, // 日租房不传
        occType: commonInfo.occType,
        roomTypeIds,
        startDate: format2DateStr(rentTimeInfo.businessDate), // 开始日期要传营业日 营业日小于等于当前日期
        strategyId: channelInfo.strategyId,
        memberLevel: commonInfo.memberLevel,
      });
      result = res.data || [];
    }
  } catch (_) {
    result = [];
  }
  return result;
};

/**
 * 更新loading状态
 * 加个timer防止重复修改时闪烁
 */
let loadingTimer;
export const setLoadingStatus: (isLoading: boolean) => void = (isLoading) => {
  if (loadingTimer) {
    clearTimeout(loadingTimer);
  }
  return actionWrapper((dispatch, getState) => {
    // 和当前状态不一样
    loadingTimer = setTimeout(() => {
      loadingTimer = null;
      if (getState().isLoading !== isLoading) {
        dispatch({
          type: CheckInByReserveAction.setLoadingStatus,
          payload: isLoading,
        });
      }
    });
  });
};

// 更新初始化状态
const setInitStatus = (isInit: boolean) => {
  return { type: CheckInByReserveAction.setInitStatus, payload: isInit };
};

// 更新数据变更状态
const setModifyStatus = (isModify: boolean) => {
  return { type: CheckInByReserveAction.setModifyStatus, payload: isModify };
};

// 更新可选房间列表
const setAllList = (list: RoomCategory[]) => {
  return {
    type: CheckInByReserveAction.setAllRoomList,
    payload: list,
  };
};

// 更新房卡读取提示
export const setCardNotice = (notice: NoticeInfo) => {
  return {
    type: CheckInByReserveAction.setCardNotice,
    payload: notice,
  };
};

// 更新房价列表
const setPriceList = (list: ConfigRoomPrice[]) => {
  return {
    type: CheckInByReserveAction.setRoomPriceList,
    payload: list,
  };
};

// 更新入住房间信息
const setRentList: (list: RentRoomInfo[]) => void = (list) => {
  return {
    type: CheckInByReserveAction.setRentRoomList,
    payload: list,
  };
};

// 更新入住时间信息
const setRentTimeInfo: (data: Partial<RentTimeInfo>) => void = (data) => {
  return { type: CheckInByReserveAction.setRentTimeInfo, payload: data };
};

// 更新宾客信息
const setGuestData: (data: CheckInGuestInfo[]) => void = (data) => {
  return {
    type: CheckInByReserveAction.setGuestData,
    payload: data,
  };
};

// 更新备注
const setRemarkInfo = (data: Partial<RemarkInfo>) => {
  return {
    type: CheckInByReserveAction.setRemarkInfo,
    payload: data,
  };
};

// 刷新可选房间列表
const refreshRoomList = () => {
  return actionWrapper(async (dispatch, getState) => {
    const state = getState();
    const { rentTimeInfo, originRoomList } = state;
    const { expectedArrivalTime, rentTime } = rentTimeInfo;
    if (expectedArrivalTime && rentTime) {
      dispatch(setLoadingStatus(true));
      const originIdMap: { [key: string]: RoomItem } = {}; // 预订单选择房间信息
      const originCategoryMap: { [roomTypeId: number]: RoomItem[] } = {}; // 预订单房型数据
      originRoomList.forEach((item) => {
        const { roomTypeId, id } = item;
        const itemKey = `${roomTypeId}|${id}`;
        if (!originIdMap[itemKey]) {
          // 房间未选中
          originIdMap[itemKey] = item;
          originCategoryMap[roomTypeId] = (originCategoryMap[roomTypeId] || []).concat(item);
        }
      });
      const roomList = await getAllRoomList(rentTimeInfo);

      dispatch(
        setAllList(
          roomList.map((category) => {
            const originSubList = originCategoryMap[category.id]; // 当前房型下有预订房间

            // 与v2.21逻辑冲突，如果有预订房间，则使用预订房 删除后端返回的房间（因为后端吴玉林返回的房间是不可选的，按理应该可选）
            let { roomList: newRoomList }: any = category;
            if (originSubList && originSubList.length > 0) {
              newRoomList = (newRoomList || []).filter((roomItem) => {
                const itemKey = `${roomItem.roomTypeId}|${roomItem.id}`;
                return !originIdMap[itemKey];
              });
            }

            if (originSubList) {
              return {
                ...category,
                roomList: originSubList.concat(newRoomList || []),
                // roomList: originSubList.concat(category.roomList || []),
              };
            }
            return category;
          }),
        ),
      );
      dispatch(setLoadingStatus(false));
    }
  });
};

// 刷新房间定价
const refreshPriceList = () => {
  return actionWrapper(async (dispatch, getState) => {
    const state = getState();
    const { rentRoomInfos } = state;
    // 没有选中房间
    if (!rentRoomInfos.length) return;
    const priceList = await getRoomPriceList(state);
    dispatch(setPriceList(priceList));
    const { cacheDepositMap } = flatPriceList(priceList);
    const currentRoomList = getState().rentRoomInfos || []; // 当前房间列表
    // 更新已选房间押金
    dispatch(
      setRentList(
        currentRoomList.map((roomItem) => {
          return {
            ...roomItem,
            deposit: cacheDepositMap[roomItem.roomTypeId],
          };
        }),
      ),
    );
  });
};

// 更新预订房间夜审状态
const updateRoomAuditPrice = () => {
  return actionWrapper(async (dispatch, getState) => {
    const businessDate = await getBusinessDate(DateJS());
    const state = getState();
    const { rentRoomInfos } = state;
    // 原始券列表
    const originCouponList: string[] = [];
    dispatch(
      setRentList(
        (rentRoomInfos || []).map((roomItem) => {
          return {
            ...roomItem,
            roomPriceList: (roomItem.roomPriceList || []).map((priceItem) => {
              // 是否过夜审
              const isBeforeAudit = DateJS(priceItem.rentDate).isBefore(businessDate, 'day');
              if (priceItem.couponNo && !isBeforeAudit) {
                // 预订时选券 且 未过夜审（未核销）
                originCouponList.push(priceItem.couponNo);
              }
              return {
                ...priceItem,
                isBeforeAudit,
              };
            }),
          };
        }),
      ),
    );
    dispatch({
      type: CheckInByReserveAction.setOriginCouponList,
      payload: originCouponList,
    });
  });
};

// 获取默认所在营业日
const getDefaultBusinessDate: (callback: () => void) => void = (callback) => {
  return actionWrapper((dispatch, getState) => {
    const { rentTimeInfo } = getState();
    dispatch(setLoadingStatus(true));
    getBusinessDate(rentTimeInfo.expectedArrivalTime)
      .then((businessDate) => {
        dispatch(setRentTimeInfo({ businessDate }));
      })
      .then(() => {
        dispatch(setLoadingStatus(false));
        if (typeof callback === 'function') {
          callback();
        }
      });
  });
};

// 页面初始化
export const initPageState: (initFunc: () => Promise<MainState>) => void = (initFunc) => {
  return async (dispatch) => {
    try {
      // 未初始化状态
      dispatch({
        type: CheckInByReserveAction.initDefaultState,
        payload: <ReducerState>{
          isInit: false,
          isLoading: true,
          isModify: false,
          commonInfo: {},
          rentTimeInfo: {},
          channelInfo: {},
          rentRoomInfos: [],
          remarkInfo: {},
          cardReadNotice: {},
          allGuestList: [],
          allRoomList: [],
          originRoomList: [],
          roomPriceList: [],
          originCouponList: [],
        },
      });
      // 获取渠道信息
      getAllChannelList().then((channelList) => {
        dispatch({ type: CheckInByReserveAction.setChannelList, payload: channelList });
      });
      // 调用初始化方法
      const initState = await initFunc();
      const originRoomList: RoomItem[] = [];
      (initState.rentRoomInfos || []).forEach((item) => {
        if (item.roomId) {
          // 不是待排房
          originRoomList.push({
            id: item.roomId,
            roomTypeId: item.roomTypeId,
            roomNo: item.roomNo,
            // 默认预订房 净房
            mainStatus: RoomMainStatus.Reserved,
            cleanStatus: RoomCleanStatus.Clean,
          });
        }
      });
      // 设置初始化数据
      dispatch({
        type: CheckInByReserveAction.initDefaultState,
        payload: { ...initState, originRoomList },
      });
      dispatch(setInitStatus(true));
      // 更新房价夜审状态
      dispatch(updateRoomAuditPrice());
      // 初始化所在营业日
      dispatch(
        getDefaultBusinessDate(() => {
          // 刷新房间定价
          dispatch(refreshPriceList());
          // 刷新可选房间列表
          dispatch(refreshRoomList());
        }),
      );
    } catch (e) {
      dispatch(setInitStatus(false));
    }
  };
};

// 清空页面
export const clearPageState: () => void = () => {
  return (dispatch) => {
    setTimeout(() => {
      dispatch(setInitStatus(false));
    }, 100);
  };
};

// 批量更新宾客手机号
export const batchUpdateGuestMobile = (updateValue: { [wyuId: string]: string }) => {
  return actionWrapper((dispatch, getState) => {
    const { allGuestList } = getState();
    dispatch(
      setGuestData(
        allGuestList.map((item) => {
          return {
            ...item,
            mobile: updateValue[item.wyuId] || item.mobile,
          };
        }),
      ),
    );
    dispatch(setModifyStatus(true));
  });
};

// 入住房间修改
export const handleRentItemChange: (indexId: string, updateValue: Partial<RentRoomInfo>) => void = (
  indexId,
  updateValue,
) => {
  return actionWrapper((dispatch, getState) => {
    const { rentRoomInfos } = getState();
    dispatch(
      setRentList(
        rentRoomInfos.map((item) => {
          if (item.indexId === indexId) {
            return {
              ...item,
              ...updateValue,
            };
          }
          return item;
        }),
      ),
    );
  });
};

// 更新备注信息
export const handleRemarkInfoChange: (data: Partial<RemarkInfo>) => void = (data) => {
  return actionWrapper((dispatch) => {
    dispatch(setRemarkInfo(data));
    dispatch(setModifyStatus(true));
  });
};

// 刷新过期房间数据
export const refreshOutdateRoomInfo = () => {
  return actionWrapper((dispatch, getState) => {
    const { rentRoomInfos } = getState();
    dispatch(
      setRentList(
        rentRoomInfos.map((item) => {
          return {
            ...item,
            // 将房间置空
            roomId: null,
            roomNo: null,
          };
        }),
      ),
    );
    // 重新拉取可用房间列表
    dispatch(refreshRoomList());
  });
};

// 新增宾客
export const handleAddGuestItem: (
  defaultData: Partial<CheckInGuestInfo>,
  callback: (data: CheckInGuestInfo) => void,
) => void = (defaultData: Partial<CheckInGuestInfo>, callback) => {
  return actionWrapper((dispatch, getState) => {
    const { allGuestList } = getState();
    const itemInfo = createGuestItem(defaultData);
    dispatch(setGuestData(allGuestList.concat(itemInfo)));
    dispatch(setModifyStatus(true));
    if (typeof callback === 'function') {
      callback(itemInfo);
    }
  });
};

// 清空已选优惠券信息
const clearAllCouponData = () => {
  return actionWrapper((dispatch, getState) => {
    const { rentRoomInfos } = getState();
    if (rentRoomInfos && rentRoomInfos.length) {
      dispatch(
        setRentList(
          rentRoomInfos.map((room) => {
            return {
              ...room,
              roomPriceList: (room.roomPriceList || []).map((item) => {
                if (item.isBeforeAudit) return item; // 已过夜审 不可修改
                return {
                  ...item,
                  ...getEmptyCouponData(),
                };
              }),
            };
          }),
        ),
      );
    }
  });
};

// 在房间已选宾客中剔除指定宾客
const removeGuestFromRooms = (indexId: string) => {
  return actionWrapper((dispatch, getState) => {
    const { rentRoomInfos } = getState();
    dispatch(
      setRentList(
        rentRoomInfos.map((item) => {
          return {
            ...item,
            guestList: filterArrByKey(item.guestList, (guestId) => guestId !== indexId),
          };
        }),
      ),
    );
  });
};

// 删除宾客 房主不会被删除
export const handleDeleteGuestItem = (indexId: string) => {
  return actionWrapper((dispatch, getState) => {
    const { allGuestList } = getState();
    const lastGuestInfo = allGuestList ? allGuestList[allGuestList.length - 1] : null;
    if (lastGuestInfo && lastGuestInfo.indexId === indexId) {
      // 删除的是最后一个宾客
      dispatch(
        setCardNotice({
          type: 'warning',
          message: renderIntlLabel('reservation_add_checkin_to_idcard_read_msg'),
        }),
      );
    }
    dispatch(setGuestData(filterArrByKey(allGuestList, (item) => item.indexId !== indexId)));
    // 在已选房间里去除这个用户
    dispatch(removeGuestFromRooms(indexId));
    dispatch(setModifyStatus(true));
  });
};

// 更新宾客信息
export const handleGuestItemChange = (indexId: string, updateData: Partial<CheckInGuestInfo>) => {
  return actionWrapper((dispatch, getState) => {
    const { allGuestList } = getState();
    let prevOwnerInfo: CheckInGuestInfo; // 之前的房主信息
    let updateGuestInfo: CheckInGuestInfo; // 当前更新的用户信息
    dispatch(
      setGuestData(
        allGuestList.map((item) => {
          if (item.memberType === MemberType.HouseOwner) {
            prevOwnerInfo = item;
          }
          if (item.indexId === indexId) {
            updateGuestInfo = {
              ...item,
              ...updateData,
            };
            return updateGuestInfo;
          }
          return item;
        }),
      ),
    );
    // 当前用户没有名字 需要在入住房间里去除掉
    if (updateGuestInfo && !updateGuestInfo.name) {
      dispatch(removeGuestFromRooms(indexId));
    }
    // 变更的是房主的信息
    if (updateGuestInfo && updateGuestInfo.memberType === MemberType.HouseOwner) {
      if (prevOwnerInfo.wyuId !== updateGuestInfo.wyuId) {
        // 20230206：保留预订人券
        const { rentRoomInfos } = getState();
        const ownerId = updateGuestInfo.wyuId; // 当前房主id
        if (rentRoomInfos && rentRoomInfos.length) {
          dispatch(
            setRentList(
              rentRoomInfos.map((room) => {
                return {
                  ...room,
                  roomPriceList: (room.roomPriceList || []).map((item) => {
                    if (item.isBeforeAudit) return item; // 已过夜审 不可修改
                    if (item.couponWyuid === ownerId) return item; // 券属于当前房主
                    return {
                      ...item,
                      ...getEmptyCouponData(),
                    };
                  }),
                };
              }),
            ),
          );
        }
      }
    }
    dispatch(setModifyStatus(true));
  });
};

// 设置为房主
export const handleSetGuestToOwner = (indexId: string) => {
  return actionWrapper((dispatch, getState) => {
    const { allGuestList } = getState();
    const updateList: CheckInGuestInfo[] = [];
    let ownerInfo: CheckInGuestInfo;
    allGuestList.forEach((item) => {
      if (item.indexId === indexId) {
        // 房主标识
        ownerInfo = {
          ...item,
          memberType: MemberType.HouseOwner,
        };
      } else {
        updateList.push({
          ...item,
          memberType: MemberType.CheckIn,
        });
      }
    });
    if (ownerInfo) {
      // 房主变更 清空已选优惠券
      dispatch(clearAllCouponData());
      // 房主放到第一位
      updateList.unshift(ownerInfo);
    }
    dispatch(setGuestData(updateList));
    dispatch(setModifyStatus(true));
  });
};

// 清空宾客信息
export const handleClearGuestItem = (indexId: string) => {
  return actionWrapper((dispatch, getState) => {
    const { allGuestList } = getState();
    const targetItem = findItemByKey(allGuestList, 'indexId', indexId);
    if (targetItem) {
      // 清空的是房主信息
      if (targetItem.memberType === MemberType.HouseOwner) {
        dispatch(clearAllCouponData()); // 清空优惠
      }
      // 当前用户姓名清空了 需要在房间信息里过滤掉
      dispatch(removeGuestFromRooms(indexId));
      // 清空当前用户基本信息
      dispatch(
        handleGuestItemChange(
          indexId,
          createGuestItem({
            // 需要保留的值
            indexId,
            memberType: targetItem.memberType,
            certType: targetItem.certType,
          }),
        ),
      );
    }
  });
};

// 入住房间宾客修改
export const handleRentItemGuestChange = (indexId: string, guestList: string[]) => {
  return actionWrapper((dispatch, getState) => {
    const { rentRoomInfos } = getState();
    const itemGuestIdMap = {}; // 当前房间已选宾客id map
    (guestList || []).forEach((guestId) => {
      itemGuestIdMap[guestId] = true;
    });
    dispatch(
      setRentList(
        rentRoomInfos.map((item) => {
          // 当前房间
          if (item.indexId === indexId) {
            return {
              ...item,
              guestList,
            };
          }
          // 其它房间
          return {
            ...item,
            // 过滤掉当前房间选择的宾客
            guestList: filterArrByKey(item.guestList, (guestId) => !itemGuestIdMap[guestId]),
          };
        }),
      ),
    );
  });
};

// 获取所有宾客
export const getAllGuestList: (callback: (list: CheckInGuestInfo[]) => void) => void = (
  callback,
) => {
  return actionWrapper((_, getState) => {
    try {
      callback(getState().allGuestList);
    } catch (e) {
      //
    }
  });
};

// 批量修改房价
export const batchUpdateRoomPrice = (
  list: { indexId: string; inputPrice: string; deposit: string }[],
) => {
  return actionWrapper(async (dispatch, getState) => {
    dispatch(setLoadingStatus(true));
    const { rentRoomInfos, commonInfo, allGuestList } = getState();
    // 更新的房价
    const updatePriceValueMap = {};
    // 更新的押金
    const updateDepositValueMap = {};
    list.forEach((item) => {
      updatePriceValueMap[item.indexId] = item.inputPrice;
      updateDepositValueMap[item.indexId] = item.deposit;
    });
    const updateCouponInfoList = []; // 重新计算金额的券列表
    const updateCouponNoMap = {}; // 重新计算金额的券map
    rentRoomInfos.forEach((roomItem) => {
      const updatePrice = updatePriceValueMap[roomItem.indexId];
      if (!updatePrice) return; // 没有修改当前房间
      (roomItem.roomPriceList || []).forEach((dayItem) => {
        if (dayItem.isBeforeAudit) return; // 已过夜审 房价和券不可修改
        const itemCouponNo = dayItem.couponNo;
        if (itemCouponNo) {
          // 间夜有使用券
          updateCouponInfoList.push({
            roomPrice: updatePrice,
            lockCouponNo: itemCouponNo,
            businessDate: dayItem.rentDate,
          });
          updateCouponNoMap[itemCouponNo] = true;
        }
      });
    });
    // 更新押金
    dispatch(
      setRentList(
        rentRoomInfos.map((roomItem) => {
          const updateDeposit = updateDepositValueMap[roomItem.indexId];
          if (!updateDeposit) return roomItem; // 没有修改当前房间
          return {
            ...roomItem,
            deposit: updateDeposit,
          };
        }),
      ),
    );
    const rentRoomList = getState().rentRoomInfos;
    const ownerInfo = findItemByKey(allGuestList, 'memberType', MemberType.HouseOwner);
    const updateCouponValueMap = await batchCalcCouponValue(
      { wyuid: ownerInfo.wyuId, occType: commonInfo.occType },
      updateCouponInfoList,
    );
    const emptyCouponData = getEmptyCouponData();
    // 更新价格和券
    dispatch(
      setRentList(
        rentRoomList.map((roomItem) => {
          const updatePrice = updatePriceValueMap[roomItem.indexId];
          if (!updatePrice) return roomItem; // 没有修改当前房间
          const itemPriceList = roomItem.roomPriceList || [];
          return {
            ...roomItem,
            roomPriceList: itemPriceList.map((dayItem) => {
              if (dayItem.isBeforeAudit) return dayItem; // 已过夜审 不可修改
              // 当前间夜券金额重新计算过
              if (updateCouponNoMap[dayItem.couponNo]) {
                // 券的新面额
                const couponFaceValue = updateCouponValueMap[dayItem.couponNo];
                return {
                  ...dayItem,
                  // 计算金额不为空 更新面值 否则清空优惠券信息
                  ...(couponFaceValue
                    ? {
                        couponAmount: couponFaceValue,
                        couponValue: parseFen2Yuan(couponFaceValue),
                      }
                    : emptyCouponData),
                  inputPrice: updatePrice,
                };
              }
              return {
                ...dayItem,
                inputPrice: updatePrice,
              };
            }),
          };
        }),
      ),
    );
    dispatch(setLoadingStatus(false));
  });
};

// 批量修改房间券
export const batchUpdateRoomCoupon = (data: Record<string, Record<string, CouponValue>>) => {
  return actionWrapper((dispatch, getState) => {
    const { rentRoomInfos } = getState();
    // 更新券
    dispatch(
      setRentList(
        rentRoomInfos.map((roomItem) => {
          const roomCouponMap = data[roomItem.indexId];
          if (!roomCouponMap) return roomItem;
          const itemPriceList = roomItem.roomPriceList || [];
          return {
            ...roomItem,
            roomPriceList: itemPriceList.map((dayItem) => {
              if (dayItem.isBeforeAudit) return dayItem; // 已过夜审 房价和券不可修改
              // 当前间夜券有调整
              if (roomCouponMap[dayItem.rentDateId]) {
                return {
                  ...dayItem,
                  ...roomCouponMap[dayItem.rentDateId],
                };
              }
              return dayItem;
            }),
          };
        }),
      ),
    );
  });
};

// 获取全部状态
export const getMainState: (callback: (state: ReducerState) => void) => void = (callback) => {
  return actionWrapper((_, getState) => {
    try {
      callback(getState());
    } catch (e) {
      //
    }
  });
};
